import {
    Component,
    Element,
    Event,
    EventEmitter,
    h,
    Host,
    Method,
    Prop,
    Watch,
} from "@stencil/core";
import { MenuItemType } from "./interface";
import {
    findMenuElements,
    findMenuItemElements,
    findMenuItemInEvent,
    findMenuItemWithSlug,
    findNextItemRecursive,
    findPrevItemRecursive,
    getIsFirstChildMenuItem,
    getMenuItemHasChildren,
    getMenuItemPath,
    getMenuItemRoot,
} from "./utils";
import { debounce } from "../../utils/debounce";

const baseClass = "uic-menu";

/**
 * @slot (default) - the `menu-item` elements belonging to the menu
 */
@Component({
    tag: "uic-menu",
    styleUrl: "menu.scss",
    shadow: false,
})
export class Menu {
    @Element() host: HTMLUicMenuElement;

    /** Name that identifies the menu */
    @Prop({ reflect: true }) name: string;

    /** Name of the currently selected menu-item */
    @Prop({ reflect: true }) current?: string;

    /** Flag that indicates whether the menu behaves as a toggle switch */
    @Prop() toggle?: boolean;

    /** Data provider for the menu child elements */
    @Prop() items: MenuItemType[] | null = null;

    /** Variant of the menu */
    @Prop() variant: "primary" | "secondary" = "primary";

    /** If set to `true` the menu items are arranged vertically */
    @Prop() vertical: boolean = false;

    /** If set to `true` the menu is collapsed when a menu item is selected */
    @Prop() autoCollapse: boolean = false;

    /** Context element selector for usage of multiple menu components inside parent context  */
    @Prop() contextElementSelector: string;

    /** Context element for usage of multiple menu components inside parent context */
    private contextElement: HTMLElement | null = null;

    /** The currently selected menu-item. */
    private selectedItem: HTMLUicMenuItemElement | null = null;

    /** ID of the animation frame request that updates the selection */
    private selectionRafId: number;

    /** Emitted when a menu item is selected or deselected */
    @Event({ eventName: "uicMenuChange", bubbles: true, cancelable: true })
    menuWillChange: EventEmitter<{
        name: string;
        current: string | null;
        element: HTMLUicMenuItemElement;
    }>;

    @Watch("current")
    @Watch("items")
    setCurrent() {
        window.cancelAnimationFrame(this.selectionRafId);
        this.selectionRafId = window.requestAnimationFrame(() => {
            this.updateSelectedItems(this.current);
        });
    }

    @Watch("contextElementSelector")
    setContextElement() {
        if (typeof this.contextElementSelector === "string") {
            this.contextElement =
                this.host.closest<HTMLElement>(this.contextElementSelector) ||
                this.host;
        } else {
            this.contextElement = this.host;
        }
    }

    /**
     * Expand all menu items in the selection tree
     */
    @Method()
    async expandSelection() {
        if (this.selectedItem === null) {
            return;
        }
        const rootItem = getMenuItemRoot(this.selectedItem);
        this.expandMenuItemTree(rootItem);
    }

    /**
     * Collapse all menu items
     */
    @Method()
    async collapseAll() {
        findMenuItemElements(this.host).forEach((element) => {
            element.expanded = false;
        });
    }

    componentWillLoad() {
        this.setContextElement();
    }

    componentDidLoad() {
        if (typeof this.current === "string") {
            this.setCurrentDebounced();
        }
    }

    private setCurrentDebounced = debounce(this.setCurrent, 100);

    /** Find uic-menu elements with same name */
    private findRelatedMenus = (): HTMLUicMenuElement[] => {
        return findMenuElements(this.contextElement || this.host, this.name);
    };

    /** Internally select or toggle a menu-item */
    private selectItem = (item: HTMLUicMenuItemElement): void => {
        if (!this.toggle && item === this.selectedItem) {
            return;
        }
        const selected = !this.toggle || !item.selected;
        const selectedItem = selected ? item : null;
        const current = selected ? item.slug : null;
        if (
            this.menuWillChange.emit({
                name: this.name,
                current: current,
                element: item,
            }).defaultPrevented
        ) {
            return;
        }
        item.selected = selected;
        this.selectedItem = selectedItem;
        this.findRelatedMenus().forEach((el) => {
            el.current = current;
        });
        this.updateSelectedItems(current, true);
    };

    /** Set the selected property of the menu-items */
    private updateSelectedItems = (
        current: string = "",
        focusRoot: boolean = false
    ): void => {
        const selectedItem = findMenuItemWithSlug(this.host, current);
        const selectedLevel = selectedItem !== null ? selectedItem.level : -1;
        const selectedPath = getMenuItemPath(selectedItem);
        const rootItem = getMenuItemRoot(selectedItem);
        findMenuItemElements(this.host).forEach((el) => {
            el.selected =
                el.slug === selectedPath[el.level] &&
                (el.level === selectedLevel || el.level === 0);
            if (!this.vertical || this.autoCollapse) {
                el.expanded = false;
            }
        });
        if (rootItem !== null && this.vertical && !this.autoCollapse) {
            this.expandMenuItemTree(rootItem);
        }
        this.selectedItem = selectedItem;
        this.updateChildSelectedText(selectedItem);
        if (focusRoot && rootItem !== null) {
            if (
                rootItem !== selectedItem &&
                !rootItem.expanded &&
                rootItem.childSelectedText
            ) {
                rootItem.focus();
            }
        }
    };

    /** Show selected state on level 0 if child items are selected */
    private updateChildSelectedText = (
        selectedItem: HTMLUicMenuItemElement | null
    ): void => {
        const rootItems = findMenuItemElements(this.host, `[level="0"]`);
        const selectedRootItem: HTMLUicMenuItemElement | null =
            selectedItem !== null && selectedItem.level > 0
                ? (selectedItem.closest(
                      ".uic-menu-item[level='0']"
                  ) as HTMLUicMenuItemElement)
                : null;
        rootItems.forEach((item) => {
            item.childSelectedText =
                item === selectedRootItem ? selectedItem.label : "";
        });
    };

    /** Click event handler */
    private handleClick = (event) => {
        const item = findMenuItemInEvent(event);
        if (item === null) {
            return;
        }
        if (getMenuItemHasChildren(item)) {
            event.preventDefault();
            item.expanded = !item.expanded;
            if (item.expanded && item.level === 0) {
                this.expandMenuItemTree(item);
            }
            return;
        }
        this.selectItem(item);
    };

    private expandMenuItemTree = (
        expandedItem: HTMLUicMenuItemElement
    ): void => {
        if (expandedItem === null || expandedItem.level > 0) {
            return;
        }
        expandedItem.expanded = true;
        this.collapseOtherMenuItems(expandedItem);
        if (this.selectedItem !== null) {
            const selectedPath = getMenuItemPath(this.selectedItem);
            findMenuItemElements(expandedItem).forEach((element) => {
                element.expanded =
                    Array.isArray(element.items) &&
                    element.items.length > 0 &&
                    selectedPath[element.level] === element.slug;
            });
        }
    };

    private collapseOtherMenuItems = (
        expandedItem: HTMLUicMenuItemElement
    ): void => {
        if (expandedItem.level > 0 || !expandedItem.expanded) {
            return;
        }
        findMenuItemElements(this.host, `[level="0"]`).forEach((element) => {
            if (element !== expandedItem) {
                element.expanded = false;
            }
        });
    };

    /** Keyboard event handler */
    private handleKeyDown = (event: KeyboardEvent) => {
        const item = findMenuItemInEvent(event);
        if (item === null) return;
        const hasChildren = getMenuItemHasChildren(item);
        const isFirstChildElement = getIsFirstChildMenuItem(item);
        const wasExpanded = item.expanded;
        switch (event.key) {
            case "Enter":
            case " ":
                event.preventDefault();
                if (hasChildren) {
                    item.expanded = !item.expanded;
                } else {
                    this.selectItem(item);
                    const rootItem: HTMLUicMenuItemElement | null =
                        item.level > 0
                            ? (item.closest(
                                  ".uic-menu-item[level='0']"
                              ) as HTMLUicMenuItemElement)
                            : null;
                    if (rootItem !== null) {
                        rootItem.focus();
                    }
                }
                break;
            case "ArrowLeft":
                event.preventDefault();
                if (!this.vertical && item.level === 0) {
                    this.focusNextItem(item, -1);
                }
                break;
            case "ArrowRight":
                event.preventDefault();
                if (!this.vertical && item.level === 0) {
                    this.focusNextItem(item, 1);
                }
                break;
            case "ArrowUp":
                event.preventDefault();
                if (this.vertical || item.level > 0) {
                    if (isFirstChildElement) {
                        this.focusNextLevel(item, -1);
                    } else {
                        const prevItem = findPrevItemRecursive(item, this.host);
                        if (prevItem !== null) {
                            prevItem.focus();
                        }
                    }
                } else if (item.level === 0 && item.expanded) {
                    item.expanded = false;
                }
                break;
            case "ArrowDown":
                event.preventDefault();
                if (this.vertical || item.level > 0) {
                    if (item.expanded) {
                        this.focusNextLevel(item, 1);
                    } else {
                        const nextItem = findNextItemRecursive(item, this.host);
                        if (nextItem !== null) {
                            nextItem.focus();
                        }
                    }
                } else if (item.level === 0 && hasChildren) {
                    if (!item.expanded) {
                        item.expanded = true;
                    } else {
                        this.focusNextLevel(item, 1);
                    }
                }
                break;
            default:
                break;
        }
        if (item.level === 0 && !wasExpanded && item.expanded) {
            this.expandMenuItemTree(item);
        }
    };

    /** Set the focus to the next menu-item on the same level */
    private focusNextItem = (
        current: HTMLUicMenuItemElement,
        dir: number
    ): void => {
        const scope = current.parent || this.contextElement || this.host;
        const items = findMenuItemElements(scope, `[level="${current.level}"]`);
        let index = items.indexOf(current) + dir;
        if (current.level === 0) {
            if (index >= items.length) {
                index = 0;
            } else if (index < 0) {
                index = items.length - 1;
            }
        }
        if (items[index] !== undefined) {
            items[index].focus();
        }
    };

    /** Set the focus to the next menu-item on parent or child level */
    private focusNextLevel = (
        current: HTMLUicMenuItemElement,
        dir: number
    ): void => {
        if (dir > 0) {
            const items = findMenuItemElements(
                current,
                `[level="${current.level + 1}"]`
            );
            if (items.length > 0) {
                items[0].focus();
            }
        } else if (dir < 0 && current.parent !== null) {
            current.parent.focus();
        }
    };

    private renderItems() {
        if (this.items === null || this.items.length === 0) {
            return;
        }
        return this.items.map((item) => (
            <uic-menu-item
                key={item.slug}
                slug={item.slug}
                label={item.label}
                icon={item.icon}
                items={item.items}
            />
        ));
    }

    render() {
        const { variant, vertical } = this;
        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--${variant}`]: variant !== undefined,
                    [`${baseClass}--horizontal`]: vertical !== true,
                    [`${baseClass}--vertical`]: vertical === true,
                }}
                onClick={this.handleClick}
                onKeyDown={this.handleKeyDown}
            >
                <div
                    class={{
                        [`${baseClass}__inner`]: true,
                    }}
                >
                    <slot />
                    {this.renderItems()}
                </div>
            </Host>
        );
    }
}
